import { lazy } from "react";
import Loadable from "components/Loadable";
import {
  routeConstants,
  FormatConstants,
  permissionConstants,
} from "services/keyConstants";
import { getStatusTypes } from "./service";
import { Description } from "@mui/icons-material";
import { getMasterDocDataByKey } from "services/ReduxUtils";
import {
  customerOrderDownload,
  customerOrderList,
} from "api/module/customer-related-process-and-distributor/order";
import {
  distributorDownload,
  distributorList,
} from "api/module/customer-related-process-and-distributor/distributor";

// Lazy-loaded components

// Order
const CreateOrder = Loadable(lazy(() => import("../Order/CreateOrder")));
const OrderForm = Loadable(lazy(() => import("../Order/OrderForm")));
const OrderReviewFormById = Loadable(
  lazy(() => import("../Order/OrderReviewFormById"))
);
const ProductDecision = Loadable(
  lazy(() => import("../Order/ProductDecision"))
);
const DistributionRecord = Loadable(
  lazy(() => import("../Order/DistributionRecord"))
);

// Distributor
const AddDistributor = Loadable(
  lazy(() => import("../Distributor/AddDistributor"))
);
const DistributorById = Loadable(
  lazy(() => import("../Distributor/DistributorById"))
);
const DistributorAgreement = Loadable(
  lazy(() => import("../Distributor/DistributorAgreement"))
);
const ListOfDistributor = Loadable(
  lazy(() => import("../Distributor/ListOfDistributor"))
);

//common
const CommonDetail = Loadable(
  lazy(() => import("components/CommonDetail/CommonDetail"))
);

// Route definition
const CRPDRoutes = [
  {
    path: routeConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION,
    children: [
      { path: "create-order", element: <CreateOrder /> },
      { path: "create-distributor", element: <AddDistributor /> },
      { path: "order-form/:id", element: <OrderForm /> },
      { path: "order-review/:id", element: <OrderReviewFormById /> },
      { path: "distributor/:id", element: <DistributorById /> },
      { path: "distributor-agreement/:id", element: <DistributorAgreement /> },
      { path: "product-decision/:id", element: <ProductDecision /> },
      { path: "distribution-record/:id", element: <DistributionRecord /> },
      {
        path: "detail/:id",
        element: (
          <CommonDetail
            navigateRoute={{
              isCondition: false,
              url: (item, filterData, masterDocData) => {
                const basePath = `/${routeConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION}/`;

                switch (masterDocData?.defaultName) {
                  case FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .ORDER_FORM:
                    return `${basePath}order-form/${item?._id}`;
                  case FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .ORDER_REVIEW_FORM:
                    return `${basePath}order-review/${item?._id}`;
                  case FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .DISTRIBUTION_RECORD:
                    return `${basePath}distribution-record/${item?._id}`;
                  case FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .DISTRIBUTOR_FORM:
                    return `${basePath}distributor/${item?._id}`;
                  case FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .DISTRIBUTOR_AGREEMENT:
                    return `${basePath}distributor-agreement/${item?._id}`;
                  default:
                    return `${basePath}${item?._id}`;
                }
              },
            }}
            url={(item, filterData, selectedRole) => {
              if (
                [
                  FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .DISTRIBUTOR_FORM,
                  FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                    .DISTRIBUTOR_AGREEMENT,
                ]?.includes(item?.defaultName)
              ) {
                return {
                  GET: distributorList,
                  DOWNLOAD: distributorDownload,
                };
              } else {
                return {
                  GET: customerOrderList,
                  DOWNLOAD: customerOrderDownload,
                };
              }
            }}
            getStatusTypes={getStatusTypes}
            filterOptions={{
              [FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                .ORDER_FORM]: [{ name: "Status", optionsKey: "statusOptions" }],
              [FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                .ORDER_REVIEW_FORM]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                .DISTRIBUTION_RECORD]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                .DISTRIBUTOR_FORM]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                .DISTRIBUTOR_AGREEMENT]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
            }}
            tableConfig={(item, filterData, selectedRole) => ({
              defaultActions: [
                FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                  .DISTRIBUTION_RECORD,
              ]?.includes(item?.defaultName)
                ? [
                    {
                      type: "view",
                      tooltip: "Go To Product Allocation",
                      color: "primary",
                      icon: <Description />,
                      key: "report",
                      auditTrail: {
                        masterDocData: getMasterDocDataByKey({
                          defaultName:
                            FormatConstants
                              .CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                              .DISTRIBUTION_RECORD,
                        }),
                      },
                      disabledObject: {
                        defaultName: item?.defaultName,
                        permissionKey: permissionConstants.UPDATE,
                      },
                      isVisible: (row) => {
                        const isResponsible =
                          row?.document?.responsibleUser?.some(
                            (u) =>
                              u?.designationId ===
                                selectedRole?.designationId &&
                              u?.userId === selectedRole?.userId
                          );

                        if (row?.recordStatus === "Initiated") {
                          return isResponsible;
                        }

                        const hasAllocated =
                          (row?.document?.allocatedData?.length || 0) > 0;
                        const hasNotAllocated =
                          (row?.document?.notAllocatedData?.length || 0) > 0;
                        const docLength = row?.document?.rows?.length || 0;

                        if (hasNotAllocated) return isResponsible;
                        if (hasAllocated && hasAllocated < docLength)
                          return isResponsible;

                        return false;
                      },
                      isCustomRoute: true,
                      getRoute: (row) => `../product-decision/${row?._id}`,
                    },
                  ]
                : undefined,
              viewActionConfig:
                item?.defaultName ===
                FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                  .DISTRIBUTION_RECORD
                  ? {
                      isVisible: (item) =>
                        (item?.document?.allocatedData?.length ?? 0) > 0,
                      isActive: (item) =>
                        (item?.document?.allocatedData?.length ?? 0) > 0,
                    }
                  : undefined,
              chip: {
                orderStatus: ({ row }) => ({
                  label: row?.orderStatus,
                  color:
                    row?.orderStatus === "Pending From Customer Side"
                      ? "warning"
                      : row?.orderStatus === "Waiting For Approval"
                      ? "info"
                      : row?.orderStatus === "Pending For Allocation"
                      ? "secondary"
                      : row?.orderStatus === "Completed"
                      ? "success"
                      : row?.orderStatus === "Rejected"
                      ? "error"
                      : row?.orderStatus === "Initiated"
                      ? "primary"
                      : row?.orderStatus === "Review"
                      ? "warning"
                      : row?.orderStatus === "Approved"
                      ? "success"
                      : row?.orderStatus === "Product Allocated"
                      ? "success"
                      : row?.orderStatus === "Product Not Allocated"
                      ? "warning"
                      : row?.orderStatus === "Product Partially"
                      ? "warning"
                      : "default",
                }),
                recordStatus: ({ row }) => ({
                  label: row?.recordStatus,
                  color:
                    row?.recordStatus === "Waiting For Approval"
                      ? "info"
                      : row?.recordStatus === "Product Not Allocated"
                      ? "warning"
                      : row?.orderStatus === "Product Partially"
                      ? "warning"
                      : row?.recordStatus === "Product Allocated"
                      ? "success"
                      : row?.recordStatus === "Accepted"
                      ? "success"
                      : row?.recordStatus === "Review"
                      ? "warning"
                      : row?.recordStatus === "Initiated"
                      ? "primary"
                      : row?.recordStatus === "Completed"
                      ? "success"
                      : row?.recordStatus === "Approved"
                      ? "success"
                      : row?.recordStatus === "Rejected"
                      ? "error"
                      : row?.recordStatus === "Request For Modified"
                      ? "info"
                      : "default",
                }),
                status: ({ row }) => ({
                  label: row?.status,
                  color:
                    row?.status === "Waiting for Agreement" ||
                    row?.status === "Waiting for review"
                      ? "warning"
                      : row?.status === "Rejected"
                      ? "error"
                      : row?.status === "Initiated"
                      ? "primary"
                      : row?.status === "Completed"
                      ? "success"
                      : row?.status === "Accepted"
                      ? "success"
                      : row?.status === "Approved"
                      ? "success"
                      : row?.status === "Waiting for Document"
                      ? "info"
                      : row?.status === "Document Approved"
                      ? "success"
                      : "default",
                }),
              },
            })}
            downloadConfig={(defaultMasterDocData, item) => {
              if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                  .ORDER_FORM
              ) {
                return {
                  documentName:
                    FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                      .ORDER_FORM,
                };
              } else if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                  .ORDER_REVIEW_FORM
              ) {
                return {
                  documentName:
                    FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                      .ORDER_REVIEW_FORM,
                };
              } else if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                  .DISTRIBUTION_RECORD
              ) {
                return {
                  documentName:
                    FormatConstants.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTION
                      .DISTRIBUTION_RECORD,
                };
              }
              return {};
            }}
          />
        ),
      },
      {
        path: "list-of-distributor/:id?",
        element: <ListOfDistributor />,
      },
    ],
  },
];

export default CRPDRoutes;
